import React, { useState, useEffect } from "react";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useCurrentUserProfile } from "../utils";
import { useDarkMode } from "../App";
import VerifyEmailDialog from "./VerifyEmailDialog";
import { onEmailNotVerified, onEmailVerified } from "../lib/emailVerificationEvents";
import "./UnverifiedEmailBanner.css";

/**
 * Persistent app-wide banner shown to logged-in users whose email hasn't been
 * verified yet. Clicking "Verify now" opens the magic-link dialog using the
 * JWT already in localStorage.
 *
 * Also acts as the centralized fallback for the unverified UX: if a gated
 * mutation is blocked server-side (EMAIL_NOT_VERIFIED), the Apollo error link
 * emits an app event that opens the same dialog here — so the user never sees
 * a raw 403. This listener is active whenever the user is logged in, even if
 * the cached profile still says verified (stale-cache case).
 *
 * Rendered once inside App.tsx (logged-in branch only).
 */
const UnverifiedEmailBanner: React.FC = () => {
  const { id, emailVerified, profile, refetch } = useCurrentUserProfile();
  const { darkMode } = useDarkMode();
  const [dialogOpen, setDialogOpen] = useState(false);

  const token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;

  // Open the dialog when a gated action is blocked server-side. Registered for
  // any logged-in user regardless of the (possibly stale) cached flag.
  useEffect(() => {
    if (!token) return undefined;
    return onEmailNotVerified(() => setDialogOpen(true));
  }, [token]);

  // Refetch the profile when the user verifies elsewhere (e.g. the magic-link
  // landing page) so the banner disappears without a reload.
  useEffect(() => {
    if (!token) return undefined;
    return onEmailVerified(() => {
      setDialogOpen(false);
      refetch();
    });
  }, [token, refetch]);

  if (!token) return null;

  const showBanner = !!id && emailVerified === false;

  return (
    <>
      {showBanner && (
        <div className={`unverified-email-banner ${darkMode ? "dark" : ""}`} role="alert">
          <div className="unverified-email-banner-inner">
            <MailOutlineIcon className="unverified-email-banner-icon" />
            <span className="unverified-email-banner-text">
              Verify your email to list cars, message sellers, and more.
            </span>
            <button
              type="button"
              className="unverified-email-banner-action"
              onClick={() => setDialogOpen(true)}
            >
              Verify now
            </button>
          </div>
        </div>
      )}
      <VerifyEmailDialog
        open={dialogOpen}
        token={token}
        email={profile?.email}
        darkMode={darkMode}
        onVerified={() => {
          setDialogOpen(false);
          refetch();
        }}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default UnverifiedEmailBanner;
